// Dead letter status badge (QA scenario 1.6.19)
//
// dead_letters.status is the lifecycle column: PENDING → REPLAYED (POST
// :id/retry also stamps replayed_at) or PENDING → DISCARDED (DELETE :id sets
// status only). The page used to derive the badge and the Retry affordance
// from replayed_at, so a DISCARDED row kept rendering as "Failed" with a live
// Retry button that the API answers with 409.

import type { DeadLetter, DeadLetterStatus } from '@/services/dead-letter.service';

export interface DeadLetterBadge {
  label: 'Failed' | 'Replayed' | 'Discarded';
  variant: 'danger' | 'success' | 'draft';
}

const BADGES: Record<DeadLetterStatus, DeadLetterBadge> = {
  PENDING: { label: 'Failed', variant: 'danger' },
  REPLAYED: { label: 'Replayed', variant: 'success' },
  DISCARDED: { label: 'Discarded', variant: 'draft' },
};

type StatusSource = Pick<DeadLetter, 'replayed_at'> & { status?: DeadLetterStatus | null };

/**
 * The status column is authoritative. replayed_at is consulted only for a row
 * that carries no status at all (older fixtures): it can tell Replayed from
 * Failed but can never identify a Discarded row.
 */
export function deadLetterStatus(letter: StatusSource): DeadLetterStatus {
  return letter.status ?? (letter.replayed_at ? 'REPLAYED' : 'PENDING');
}

export function deadLetterBadge(letter: StatusSource): DeadLetterBadge {
  return BADGES[deadLetterStatus(letter)];
}

/** Retry / Discard apply to PENDING rows only — REPLAYED and DISCARDED are terminal (409). */
export function isDeadLetterOpen(letter: StatusSource): boolean {
  return deadLetterStatus(letter) === 'PENDING';
}
